import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { faArrowRight, faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { File } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Mention, MentionsInput } from 'react-mentions';
import { useSearchParams } from 'react-router-dom';

import { Jurisdiction } from '../../../../../../../../../backend/src/types/types';
import { capitalizeFirstLetter } from '../../../../../../../helpers/filterNames';
import { CLAIM_DOC_TYPES } from '../../../constant';
import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { DocIds } from '@/api/types/backend-dto-types';
import { Button } from '@/components/atoms/new-button';
import Dropdown, { DropdownOption } from '@/components/molecules/Dropdown';
import Modal from '@/components/molecules/Modals/CustomModal';
import { MentionsInputStyleKim } from '@/styles';
import { DraftType, ExecuteKimSearch, KimMode } from '@/types';

interface MentionInterface {
  id: string;
  display: string;
  childIndex: number;
  index: number;
  plainTextIndex: number;
}

interface QuestionInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  onExecuteCall?: ExecuteKimSearch;
  docs: DocIds[];
  taggedDocuments: { doc_id: string; file_name: string }[];
  setTaggedDocuments: (v: { doc_id: string; file_name: string }[]) => void;
  mode: KimMode;
  isCreatingThread: boolean;
  draftType: DraftType | null;
  setDraftType: (draftType: DraftType | null) => void;
  jurisdiction: Jurisdiction[];
}

const QuestionInput: React.FC<QuestionInputProps> = ({
  value,
  onChange,
  placeholder,
  onExecuteCall,
  docs,
  mode,
  taggedDocuments,
  setTaggedDocuments,
  isCreatingThread,
  draftType,
  setDraftType,
  jurisdiction,
}) => {
  const { t } = useTranslation();

  const [isFocused, setIsFocused] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const mentionsInputRef = useRef<any>(null);

  // Check search params
  const [searchParams] = useSearchParams();
  const selectedDoc = searchParams.get('selectedDocId');
  const processedDocRef = useRef<string | null>(null);

  const taggableDocs = useMemo(() => {
    if (draftType === 'deposition') {
      return docs.filter((doc) => doc.type && CLAIM_DOC_TYPES.includes(doc.type.toLowerCase()));
    }
    return docs;
  }, [draftType, docs]);

  const draftTypeOptions: DropdownOption<DraftType>[] = [
    { label: `${capitalizeFirstLetter(t('letter of claim'))} outline`, key: 'claim' as DraftType },
    { label: 'Letter of rebuttal outline', key: 'rebuttal' as DraftType },
    ...(jurisdiction.length === 0
      ? [
          { label: 'Deposition summary', key: 'deposition' as DraftType },
          { label: 'Witness statement summary', key: 'deposition' as DraftType },
        ]
      : (jurisdiction
          .map((j) => {
            if (j === Jurisdiction.USFederal) {
              return { label: 'Deposition summary', key: 'deposition' as DraftType };
            }
            if (j === Jurisdiction.EnglandAndWales) {
              return { label: 'Witness statement summary', key: 'deposition' as DraftType };
            }
          })
          .filter(Boolean) as DropdownOption<DraftType>[])),
  ];

  useEffect(() => {
    if (selectedDoc && selectedDoc !== processedDocRef.current) {
      const docName = docs.find((doc) => doc.id === selectedDoc)?.file.name;
      if (!docName) return;
      setTaggedDocuments([{ doc_id: selectedDoc, file_name: docName }]);
      onChange(`@[${docName}](${selectedDoc}) `);

      if (mentionsInputRef.current) {
        const textarea = mentionsInputRef.current;
        textarea.setSelectionRange(textarea.value.length, textarea.value.length);
        textarea.focus();
      }

      processedDocRef.current = selectedDoc;
    }
  }, [selectedDoc, docs, setTaggedDocuments, onChange]);

  // Handlers
  const executeSearch = () => onExecuteCall && onExecuteCall({ question: value, draftType, questionMode: mode });
  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (value.trim()) {
        executeSearch();
      }
    }
  };

  const renderTruncatedMention = (id: string, display: string) => {
    return display.length > 20 ? `${display.substring(0, 20)}...` : display;
  };

  const handleChange = ({ newValue, mentions }: { newValue: string; mentions: MentionInterface[] }) => {
    const uniqueTaggedDocs = mentions.reduce(
      (acc, mention) => {
        if (!acc.some((doc) => doc.doc_id === mention.id)) {
          acc.push({ doc_id: mention.id, file_name: mention.display });
        }
        return acc;
      },
      [] as { doc_id: string; file_name: string }[],
    );

    setTaggedDocuments(uniqueTaggedDocs);
    onChange(newValue);
  };

  const triggerMentions = useCallback(() => {
    if (mentionsInputRef.current) {
      const textarea = mentionsInputRef.current;
      const currentValue = value;
      const cursorPosition = textarea.selectionStart;

      // Insert '@' at the cursor position
      const newValue = currentValue + '@';
      onChange(newValue);

      // Use requestAnimationFrame to ensure the DOM has updated
      requestAnimationFrame(() => {
        // Set cursor position after the inserted '@'
        textarea.setSelectionRange(cursorPosition + 1, cursorPosition + 1);
        textarea.focus();

        // Simulate typing '@' by dispatching events
        const inputEvent = new InputEvent('input', { bubbles: true });
        textarea.dispatchEvent(inputEvent);

        const keydownEvent = new KeyboardEvent('keydown', { key: '@', bubbles: true });
        textarea.dispatchEvent(keydownEvent);
      });
    }
  }, [onChange, value]);

  const sanitizeFileName = (fileName: string) => {
    return fileName.replace(/[[]()]/g, '');
  };

  const handleDraftTypeChange = (newDraftType: DraftType | null) => {
    if (newDraftType === 'deposition') {
      // Prefill the question and show document selection modal
      onChange('Create a deposition summary for...');
      setShowDocumentModal(true);
    }
    setDraftType(newDraftType);
  };

  const handleDocumentSelection = () => {
    setDraftType('deposition');
    setShowDocumentModal(false);
  };

  const toggleDocumentSelection = (doc: DocIds) => {
    const isAlreadyTagged = taggedDocuments.some((taggedDoc) => taggedDoc.doc_id === doc.id);

    if (isAlreadyTagged) {
      // Remove the document mention from the input
      const mentionToRemove = `@[${sanitizeFileName(doc.file.name)}](${doc.id})`;
      const newValue = value.replace(mentionToRemove, '').trim();
      onChange(newValue);

      // Update tagged documents
      const updatedTaggedDocs = taggedDocuments.filter((taggedDoc) => taggedDoc.doc_id !== doc.id);
      setTaggedDocuments(updatedTaggedDocs);
    } else {
      // Add the document mention to the input
      const mentionToAdd = `@[${sanitizeFileName(doc.file.name)}](${doc.id})`;
      const currentValue = value || 'Create a deposition summary for...';
      const newValue = currentValue.replace('...', `... ${mentionToAdd}`);
      onChange(newValue);

      // Update tagged documents
      const updatedTaggedDocs = [...taggedDocuments, { doc_id: doc.id, file_name: doc.file.name }];
      setTaggedDocuments(updatedTaggedDocs);
    }
  };

  return (
    <div className="relative flex w-full flex-col items-center gap-4">
      <div
        className={`flex w-full flex-col gap-1 rounded-lg border-2 px-2 pt-4 pb-2 transition-all duration-300 ${
          isFocused ? 'border-gray-300' : 'border-gray-200'
        }`}
      >
        <div className="relative min-h-16">
          <div className="max-h-56">
            <MentionsInput
              value={value}
              onChange={(e, newValue, newPlainTextValue, mentions) => handleChange({ newValue, mentions })}
              placeholder={placeholder || 'Ask Kim..'}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              a11ySuggestionsListLabel={'Suggested mentions'}
              onKeyDown={onKeyDown}
              inputRef={mentionsInputRef}
              style={MentionsInputStyleKim}
              disabled={isCreatingThread}
            >
              <Mention
                trigger="@"
                data={taggableDocs.map((doc) => ({
                  id: doc.id,
                  display: sanitizeFileName(doc.file.name),
                }))}
                appendSpaceOnAdd={true}
                displayTransform={renderTruncatedMention}
                style={{
                  backgroundColor: '#EEEEEE',
                  borderRadius: '2px',
                }}
                onAdd={() => trackCustomEvent(TrackingEvents.KimTagDocument)}
              />
            </MentionsInput>
          </div>
          {mode === KimMode.DraftFlow && (
            <div className="w-fit">
              <Dropdown<DraftType>
                id="draftType"
                placeholder="Draft type (optional)"
                buttonIcon={<File className="h-3 w-3" />}
                options={draftTypeOptions}
                onOptionClick={handleDraftTypeChange}
                onClearClick={() => setDraftType(null)}
                value={draftType}
                className="mt-2"
              />
            </div>
          )}
        </div>

        <div className="flex items-end justify-between gap-2 pr-1">
          <button
            className="flex cursor-pointer items-center text-xs text-gray-500 italic hover:text-gray-700"
            onClick={triggerMentions}
          >
            @ Documents
          </button>
          <button
            className="bg-brandSecondary hover:bg-brandSecondary-hover flex h-7 w-7 cursor-pointer items-center justify-center rounded-full px-[9px] py-2 text-sm font-semibold text-white disabled:bg-gray-500"
            onClick={executeSearch}
          >
            {isCreatingThread ? (
              <FontAwesomeIcon icon={faCircleNotch} className="fa-spin text-white" />
            ) : (
              <FontAwesomeIcon icon={faArrowRight} className="text-white" />
            )}
          </button>
        </div>
      </div>

      {/* Document Selection Modal */}
      <Modal
        content={
          <div className="p-6">
            <div className="mb-4">
              <p className="mb-2 text-sm text-gray-600">
                Select the documents you want to include in your summary. Only claim-related documents are available for
                this type of draft.
              </p>
              <div className="mb-4 text-xs text-gray-500">
                <strong>Available document types:</strong> {CLAIM_DOC_TYPES.join(', ')}
              </div>
            </div>

            <div className="max-h-96 overflow-y-auto rounded-lg border border-gray-200">
              {taggableDocs.length === 0 ? (
                <div className="p-8 text-center text-sm text-gray-500">
                  <p>No claim-related documents found.</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {taggableDocs.map((doc) => (
                    <div
                      key={doc.id}
                      className={`cursor-pointer p-4 transition-colors hover:bg-gray-50 ${
                        taggedDocuments.some((taggedDoc) => taggedDoc.doc_id === doc.id)
                          ? 'bg-brandSecondary/5 border-brandSecondary border-l-4'
                          : ''
                      }`}
                      onClick={() => toggleDocumentSelection(doc)}
                    >
                      <div className="flex items-center">
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">{doc.file.name}</div>
                          <div className="mt-1 text-xs text-gray-500">Type: {doc.type}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-6 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                {taggedDocuments.length} document{taggedDocuments.length !== 1 ? 's' : ''} selected
              </div>
              <div className="flex gap-3">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    // Clear all tagged documents and reset the input
                    setTaggedDocuments([]);
                    onChange('');
                    setShowDocumentModal(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="brand"
                  size="sm"
                  onClick={handleDocumentSelection}
                  disabled={taggedDocuments.length === 0}
                >
                  Done
                </Button>
              </div>
            </div>
          </div>
        }
        isOpen={showDocumentModal}
        handleClose={() => {
          setShowDocumentModal(false);
        }}
        maxWidth="60rem"
      />
    </div>
  );
};

export default React.memo(QuestionInput);
